import {
  AfterViewInit,
  Directive,
  ElementRef,
  HostListener,
  inject,
  Input,
  OnDestroy,
} from "@angular/core";

let nextTooltipId = 0;

@Directive({
  selector: "[appOverflowTooltip]",
  standalone: true,
})
export class OverflowTooltipDirective implements AfterViewInit, OnDestroy {
  @Input() appOverflowTooltip: unknown = null;
  @Input() overflowTooltipTarget: string | null = null;
  @Input() overflowTooltipDelegate: string | null = null;
  @Input() overflowTooltipDisabled = false;

  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;
  private resizeObserver?: ResizeObserver;
  private activeTarget: HTMLElement | null = null;
  private tooltipElement: HTMLElement | null = null;
  private originalAriaDescribedBy: string | null = null;
  private readonly tooltipId = `app-overflow-tooltip-${++nextTooltipId}`;
  private readonly reposition = (): void => this.positionTooltip();

  ngAfterViewInit(): void {
    if (typeof ResizeObserver !== "undefined") {
      this.resizeObserver = new ResizeObserver(() => this.closeIfNoLongerOverflowing());
      this.resizeObserver.observe(this.host);
    }
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.close();
  }

  @HostListener("document:mouseover", ["$event"])
  onMouseOver(event: MouseEvent): void {
    const target = this.resolveEventTarget(event.target);

    if (target !== null && !this.isInsideActiveTarget(event.relatedTarget)) {
      this.openFor(target);
    }
  }

  @HostListener("document:mouseout", ["$event"])
  onMouseOut(event: MouseEvent): void {
    if (!this.isInsideActiveTarget(event.relatedTarget)) {
      this.close();
    }
  }

  @HostListener("document:focusin", ["$event"])
  onFocusIn(event: FocusEvent): void {
    const target = this.resolveEventTarget(event.target);

    if (target !== null) {
      this.openFor(target);
    }
  }

  @HostListener("document:focusout", ["$event"])
  onFocusOut(event: FocusEvent): void {
    if (!this.isInsideActiveTarget(event.relatedTarget)) {
      this.close();
    }
  }

  @HostListener("document:input", ["$event"])
  onInput(event: Event): void {
    const target = this.resolveEventTarget(event.target);

    if (target !== null && target.ownerDocument.activeElement === target) {
      this.openFor(target);
    }
  }

  private openFor(target: HTMLElement): void {
    const content = this.resolveContent(target);

    if (
      this.overflowTooltipDisabled ||
      this.isConcealedInput(target) ||
      content === "" ||
      !this.isOverflowing(target)
    ) {
      this.close();
      return;
    }

    if (this.activeTarget === target && this.tooltipElement !== null) {
      this.tooltipElement.textContent = content;
      this.positionTooltip();
      return;
    }

    this.close();
    this.activeTarget = target;
    this.originalAriaDescribedBy = target.getAttribute("aria-describedby");

    const tooltip = target.ownerDocument.createElement("div");
    tooltip.id = this.tooltipId;
    tooltip.className = "app-overflow-tooltip";
    tooltip.setAttribute("role", "tooltip");
    tooltip.textContent = content;
    target.ownerDocument.body.appendChild(tooltip);
    this.tooltipElement = tooltip;
    target.setAttribute(
      "aria-describedby",
      [this.originalAriaDescribedBy, this.tooltipId].filter(Boolean).join(" "),
    );

    this.positionTooltip();
    window.addEventListener("resize", this.reposition);
    window.addEventListener("scroll", this.reposition, true);
  }

  private close(): void {
    window.removeEventListener("resize", this.reposition);
    window.removeEventListener("scroll", this.reposition, true);

    if (this.activeTarget !== null) {
      if (this.originalAriaDescribedBy !== null && this.originalAriaDescribedBy !== "") {
        this.activeTarget.setAttribute("aria-describedby", this.originalAriaDescribedBy);
      } else {
        this.activeTarget.removeAttribute("aria-describedby");
      }
    }

    this.tooltipElement?.remove();
    this.tooltipElement = null;
    this.activeTarget = null;
    this.originalAriaDescribedBy = null;
  }

  private positionTooltip(): void {
    if (this.activeTarget === null || this.tooltipElement === null) {
      return;
    }

    const targetRect = this.activeTarget.getBoundingClientRect();
    const tooltipRect = this.tooltipElement.getBoundingClientRect();
    const viewportPadding = 8;
    const gap = 8;
    const idealLeft = targetRect.left + (targetRect.width - tooltipRect.width) / 2;
    const maxLeft = window.innerWidth - tooltipRect.width - viewportPadding;
    const left = Math.max(viewportPadding, Math.min(idealLeft, maxLeft));
    const showBelow = targetRect.top < tooltipRect.height + gap + viewportPadding;
    const top = showBelow
      ? targetRect.bottom + gap
      : targetRect.top - tooltipRect.height - gap;

    this.tooltipElement.classList.toggle("app-overflow-tooltip--below", showBelow);
    this.tooltipElement.style.left = `${left}px`;
    this.tooltipElement.style.top = `${Math.max(viewportPadding, top)}px`;
  }

  private closeIfNoLongerOverflowing(): void {
    if (this.activeTarget !== null && !this.isOverflowing(this.activeTarget)) {
      this.close();
    }
  }

  private resolveEventTarget(eventTarget: EventTarget | null): HTMLElement | null {
    if (this.overflowTooltipDelegate !== null && this.overflowTooltipDelegate !== "") {
      const element = eventTarget instanceof Element
        ? eventTarget.closest<HTMLElement>(this.overflowTooltipDelegate)
        : null;

      return element && this.host.contains(element) ? element : null;
    }

    if (this.overflowTooltipTarget !== null && this.overflowTooltipTarget !== "") {
      return this.host.querySelector<HTMLElement>(this.overflowTooltipTarget);
    }

    return this.host;
  }

  private resolveContent(target: HTMLElement): string {
    const explicitContent = typeof this.appOverflowTooltip === 'string' ||
      typeof this.appOverflowTooltip === 'number'
      ? String(this.appOverflowTooltip).trim()
      : "";

    if (explicitContent !== "") {
      return explicitContent;
    }

    if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) {
      return target.value.trim();
    }

    return (target.textContent ?? "").trim();
  }

  private isOverflowing(target: HTMLElement): boolean {
    if (
      target.scrollWidth > target.clientWidth + 1 ||
      target.scrollHeight > target.clientHeight + 1
    ) {
      return true;
    }

    const content = this.resolveContent(target);
    const availableWidth = this.resolveVisibleWidth(target);

    if (content === "" || availableWidth <= 0) {
      return false;
    }

    return this.measureTextWidth(content, target) > availableWidth + 1;
  }

  private isConcealedInput(target: HTMLElement): boolean {
    return target instanceof HTMLInputElement && target.type === "password";
  }

  private resolveVisibleWidth(target: HTMLElement): number {
    const targetStyle = getComputedStyle(target);
    const horizontalPadding =
      Number.parseFloat(targetStyle.paddingLeft || "0") +
      Number.parseFloat(targetStyle.paddingRight || "0");
    let visibleWidth = Math.max(0, target.clientWidth - horizontalPadding);
    let ancestor = target.parentElement;
    let inspectedAncestors = 0;

    while (ancestor && ancestor !== this.host && inspectedAncestors < 4) {
      const ancestorStyle = getComputedStyle(ancestor);
      const clipsHorizontally = ["auto", "clip", "hidden", "scroll"].includes(
        ancestorStyle.overflowX,
      );

      if (clipsHorizontally && ancestor.clientWidth > 0) {
        visibleWidth = visibleWidth > 0
          ? Math.min(visibleWidth, ancestor.clientWidth)
          : ancestor.clientWidth;
      }

      ancestor = ancestor.parentElement;
      inspectedAncestors += 1;
    }

    return visibleWidth;
  }

  private measureTextWidth(content: string, target: HTMLElement): number {
    const canvas = target.ownerDocument.createElement("canvas");
    const context = canvas.getContext("2d");

    if (!context) {
      return 0;
    }

    const style = getComputedStyle(target);
    context.font = [
      style.fontStyle,
      style.fontWeight,
      style.fontSize,
      style.fontFamily,
    ].filter(Boolean).join(" ");

    return context.measureText(content).width;
  }

  private isInsideActiveTarget(candidate: EventTarget | null): boolean {
    return candidate instanceof Node && this.activeTarget?.contains(candidate) === true;
  }
}
